import { SupportedServices } from 'env/types';
import { isObject } from 'src/utils/typeguards/isObject';
import { fetchier } from './fetchier';
import { FetchierConfig, FetchierConfigWithHeaders } from './fetchier/types';
import { getServerURLs } from './utils/getServerURLs';
import { processServerQueryParameters } from './utils/processServerQueryParameters';
import {
    SupportedMethod,
    SUPPORTED_HTTP_METHODS,
    MAX_URL_LENGTH,
    DEFAULT_TIMEOUT,
    RESPONSE_TYPES,
    SUPPORTED_CONTENT_TYPES,
} from './constants';
import {
    APIActionParams,
    SuccessfulResponse,
    NetworkErrorResponse,
    APIPromiseProvider,
    InternalAPIActionParams,
} from './types';

interface APIServiceMethods extends Record<SupportedMethod, APIPromiseProvider> {
    baseURL: string;
}
export class APIService implements APIServiceMethods {
    baseURL: string;
    POST: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: APIActionParams<TBody>
    ) => Promise<SuccessfulResponse<TResponseType> | NetworkErrorResponse>;
    PUT: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: APIActionParams<TBody>
    ) => Promise<SuccessfulResponse<TResponseType> | NetworkErrorResponse>;
    DELETE: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: APIActionParams<TBody>
    ) => Promise<SuccessfulResponse<TResponseType> | NetworkErrorResponse>;
    GET: <TResponseType = Record<string, unknown>, TBody = unknown>(
        givenparams: APIActionParams<TBody>
    ) => Promise<SuccessfulResponse<TResponseType> | NetworkErrorResponse>;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
        this.POST = async <TResponseType = Record<string, unknown>, TBody = unknown>(params: APIActionParams<TBody>) =>
            this.promise<TResponseType>({ ...params, method: SUPPORTED_HTTP_METHODS.POST });
        this.PUT = async <TResponseType = Record<string, unknown>, TBody = unknown>(params: APIActionParams<TBody>) =>
            this.promise<TResponseType>({ ...params, method: SUPPORTED_HTTP_METHODS.PUT });
        this.DELETE = async <TResponseType = Record<string, unknown>, TBody = unknown>(
            params: APIActionParams<TBody>
        ) => this.promise<TResponseType>({ ...params, method: SUPPORTED_HTTP_METHODS.DELETE });
        this.GET = async <TResponseType = Record<string, unknown>, TBody = unknown>(params: APIActionParams<TBody>) =>
            this.promise<TResponseType>({ ...params, method: SUPPORTED_HTTP_METHODS.GET });
    }

    generateServiceURL(
        action: string,
        options: {
            params?: FetchierConfig['body'];
            useDotNotation?: boolean;
        }
    ) {
        const { params, useDotNotation = false } = options;
        const parsedParams = processServerQueryParameters(params, useDotNotation);
        return `${this.baseURL}/${action}${parsedParams.length ? `?${parsedParams}` : ''}`;
    }

    _resolveActionEndpoint(method: SupportedMethod, params: APIActionParams) {
        const joinedParams = {
            ...(isObject(params.queryParams) ? params.queryParams : {}),
            ...(isObject(params.input) ? params.input : {}),
        };
        return this.generateServiceURL(params.action, {
            useDotNotation: method !== SUPPORTED_HTTP_METHODS.GET || !!params.useDotNotation,
            /**
             * In cases where the method is POST, we don't want the input to end up in the query param
             * in case the input (which goes into the body) has values which shouldn't be exposed in the
             * URL
             */
            params: method === SUPPORTED_HTTP_METHODS.POST ? params.queryParams : joinedParams,
        });
    }

    _formatInput(contentType: string, input: Record<string, unknown> | BodyInit) {
        switch (contentType) {
            case SUPPORTED_CONTENT_TYPES.TEXT:
            case SUPPORTED_CONTENT_TYPES.JSON:
                /**
                 * Text Plain and JSON we leave alone. Normally text/plain we would stringify our objects,
                 * but because our services actually use text/plain as application/json (to circumvent
                 * CORS restrictions on POST requests with application/json), we leave it alone
                 */
                return input;
            case SUPPORTED_CONTENT_TYPES.FORM_DATA: {
                if (input instanceof FormData) {
                    return input;
                }
                const form = new FormData();
                Object.entries(input).forEach(([field, value]) => {
                    form.append(field, value);
                });
                return form;
            }
            case SUPPORTED_CONTENT_TYPES.URL_FORM_ENCODED: {
                const form = new URLSearchParams();
                Object.entries(input).forEach(([field, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach(subValue => {
                            form.append(`${field}[]`, subValue);
                        });
                    } else {
                        form.append(field, value);
                    }
                });
                return form.toString();
            }
            default:
                return input;
        }
    }

    _generateFetchOptions(
        method: SupportedMethod,
        { timeout = DEFAULT_TIMEOUT, headers, input = {}, accessToken }: APIActionParams
    ) {
        const fetchOpts: FetchierConfigWithHeaders = {
            method,
            timeout,
            headers: headers ?? ({} as Record<string, string>),
        };

        if (!(input instanceof FormData) && !fetchOpts.headers['Content-Type']) {
            // "Simple" CORS requires POST Content-Type be one of:
            //   - application/x-www-form-urlencoded
            //   - multipart/form-data
            //   - text/plain
            // We use text/plain as it is the "least wrong" of the three.
            // Our API servers ignore the Content-Type header, so this
            // doesn't cause any issues. When our request body is FormData
            // we need to let the browser set this header with the
            // appropriate boundary directive
            fetchOpts.headers['Content-Type'] = SUPPORTED_CONTENT_TYPES.TEXT;
        }
        if (method !== SUPPORTED_HTTP_METHODS.GET) {
            fetchOpts.body = this._formatInput(fetchOpts.headers['Content-Type'], input);
        }
        if (accessToken) {
            fetchOpts.headers = {
                ...fetchOpts.headers,
                Authorization: `Bearer ${accessToken}`,
            };
        }
        return fetchOpts;
    }

    _sanityCheckHTTPVerb(method: SupportedMethod, url: string) {
        if (method !== SUPPORTED_HTTP_METHODS.GET) {
            return method;
        }
        // If the GET URL is really long, switch to POST to avoid issues
        // with browser or server URL length limits.
        return url.length > MAX_URL_LENGTH ? SUPPORTED_HTTP_METHODS.POST : method;
    }

    async promise<TResponseType = Record<string, unknown>, TBody = unknown>(
        params: InternalAPIActionParams<TBody>
    ): Promise<SuccessfulResponse<TResponseType> | NetworkErrorResponse> {
        const url = this._resolveActionEndpoint(params.method, params);
        const sanityCheckedMethod = this._sanityCheckHTTPVerb(params.method, url);
        const options = this._generateFetchOptions(sanityCheckedMethod, params);

        const response = await fetchier(url, options);
        try {
            const text = await response.text();
            const data = !text ? {} : JSON.parse(text);
            const successfulResponse: SuccessfulResponse<TResponseType> = {
                type: RESPONSE_TYPES.RESOLVED,
                data,
                statusCode: response.status,
            };
            return successfulResponse;
        } catch (error) {
            throw error;
        }
    }
}
export type APIServices = Record<keyof SupportedServices, APIService>;
const serverUrls = getServerURLs();

export const API: APIServices = {
    graphql: new APIService(serverUrls.graphql),
    max: new APIService(serverUrls.max),
    settings: new APIService(serverUrls.settings),
    zillowAuth: new APIService(serverUrls.zillowAuth),
    zillowAuth0: new APIService(serverUrls.zillowAuth0),
    zillowDomain: new APIService(serverUrls.zillowDomain),
};
